import React, { useState, useRef, useEffect, useCallback } from 'react';
import config from '../config';
import '../styles/EmailComposer.css';

// ═══════════════════════════════════════════════════════════════
// ContactInput — autocomplete input for To / CC fields
// Fetches contacts from /api/contacts/search?q=...
// ═══════════════════════════════════════════════════════════════

const ContactInput = ({ label, value, onChange, authToken, placeholder }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Parse value string into array of chips
  const chips = (value || '').split(/[,;]/).map(s => s.trim()).filter(Boolean);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchContacts = useCallback(async (q) => {
    if (!q || q.length < 2 || !authToken) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/contacts/search?q=${encodeURIComponent(q)}&limit=8`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      const data = await res.json();
      if (data.success && data.contacts) {
        // Filter out already selected emails
        const existing = new Set(chips.map(c => c.toLowerCase()));
        setSuggestions(data.contacts.filter(c => !existing.has(c.email.toLowerCase())));
        setShowDropdown(true);
      }
    } catch (err) {
      console.error('Contact search error:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken, chips]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    // Check if user typed a delimiter — means they finished an email
    if (val.endsWith(',') || val.endsWith(';') || val.endsWith(' ')) {
      const newEmail = val.slice(0, -1).trim();
      if (newEmail && newEmail.includes('@')) {
        const updated = [...chips, newEmail].join(', ');
        onChange(updated);
        setQuery('');
        setShowDropdown(false);
        return;
      }
    }

    // Debounced search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchContacts(val.trim()), 300);
  };

  const selectContact = (contact) => {
    const updated = [...chips, contact.email].join(', ');
    onChange(updated);
    setQuery('');
    setShowDropdown(false);
    setSuggestions([]);
  };

  const removeChip = (idx) => {
    const updated = chips.filter((_, i) => i !== idx).join(', ');
    onChange(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      e.preventDefault();
      if (query.trim().includes('@')) {
        const updated = [...chips, query.trim()].join(', ');
        onChange(updated);
        setQuery('');
        setShowDropdown(false);
      }
    }
    if (e.key === 'Backspace' && !query && chips.length > 0) {
      removeChip(chips.length - 1);
    }
  };

  return (
    <div className="ec-field" ref={wrapperRef}>
      <label className="ec-label">{label}</label>
      <div className="ec-contact-input">
        <div className="ec-chips-area">
          {chips.map((chip, idx) => (
            <span key={idx} className="ec-chip">
              {chip}
              <button type="button" onClick={() => removeChip(idx)} className="ec-chip-x">×</button>
            </span>
          ))}
          <input
            type="text"
            className="ec-chip-input"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
            placeholder={chips.length === 0 ? placeholder : ''}
          />
        </div>
        {loading && <div className="ec-contact-loader" />}

        {showDropdown && suggestions.length > 0 && (
          <div className="ec-contact-dropdown">
            {suggestions.map((c, idx) => (
              <button key={idx} className="ec-contact-option" onClick={() => selectContact(c)} type="button">
                <div className="ec-contact-avatar">{(c.name || c.email).charAt(0).toUpperCase()}</div>
                <div className="ec-contact-info">
                  <span className="ec-contact-name">{c.name || c.email}</span>
                  <span className="ec-contact-email">{c.email}</span>
                  {c.title && <span className="ec-contact-title">{c.title}{c.department ? ` · ${c.department}` : ''}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// EmailComposer — Main modal component
// ═══════════════════════════════════════════════════════════════

const EmailComposer = ({ isOpen, onClose, messageData, authToken, userEmail }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromMode, setFromMode] = useState('system'); // 'system' | 'self'
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const recognitionRef = useRef(null);

  // ── Initialize fields when modal opens (NO auto-compose) ──
  useEffect(() => {
    if (messageData && isOpen) {
      setTo('');
      setCc('');
      setFromMode('system');
      setFromEmail('');
      setError('');
      setSuccess(false);
      // ◀── Point 4: Use question as basic subject, leave body empty
      // AI compose is now triggered manually via button
      const q = messageData?.question || '';
      setSubject(q.length > 100 ? q.substring(0, 100) + '…' : q || 'VBL Sales AI — Shared Response');
      setBody('');
      setComposing(false);

      // ◀── Point 1: Fetch allowed domains
      fetchAllowedDomains();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageData, isOpen]);

  // ◀── Point 1: Allowed domains state
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [restrictDomains, setRestrictDomains] = useState(false);

  const fetchAllowedDomains = async () => {
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/share/allowed-domains`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (data.success) {
        setAllowedDomains(data.allowed_domains || []);
        setRestrictDomains(data.restrict_domains || false);
      }
    } catch (err) {
      console.warn('Could not fetch allowed domains:', err);
    }
  };

  // ◀── Point 1: Validate email domain
  const validateEmailDomain = (email) => {
    if (!restrictDomains || allowedDomains.length === 0) return true;
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && allowedDomains.map(d => d.toLowerCase()).includes(domain);
  };

  // ◀── Point 4: Manual compose trigger
  const handleAICompose = async () => {
    if (!messageData) return;
    autoCompose(messageData);
  };

  const autoCompose = async (data) => {
    setComposing(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/share/compose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          question: data?.question || '',
          response_summary: (data?.response || '').substring(0, 500),
          data_row_count: data?.data?.length || 0,
        }),
      });
      const result = await res.json();
      if (result.success && result.subject) {
        setSubject(result.subject);
        setBody(result.body || '');
      } else {
        // Fallback — use question as subject
        const q = data?.question || '';
        setSubject(q.length > 100 ? q.substring(0, 100) + '…' : q || 'VBL Sales AI — Shared Response');
      }
    } catch (err) {
      console.warn('Auto-compose failed, using fallback:', err);
      const q = data?.question || '';
      setSubject(q.length > 100 ? q.substring(0, 100) + '…' : q || 'VBL Sales AI — Shared Response');
    } finally {
      setComposing(false);
    }
  };

  // ── Close on Escape ─────────────────────────────────────
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // ── Speech-to-text ──────────────────────────────────────
  const startRecording = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';

    let finalTranscript = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += t + ' ';
        else interim += t;
      }
      setBody(prev => {
        const base = prev.replace(/\u200B.*$/, '');
        return (base ? base + ' ' : '') + finalTranscript + (interim ? '\u200B' + interim : '');
      });
    };
    recognition.onerror = (e) => {
      setIsRecording(false);
      if (e.error === 'not-allowed') setError('Microphone access denied.');
    };
    recognition.onend = () => {
      setIsRecording(false);
      setBody(prev => prev.replace(/\u200B/g, ''));
    };
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    setIsRecording(false);
  }, []);

  const toggleVoice = () => { isRecording ? stopRecording() : startRecording(); };

  // ── Send email ──────────────────────────────────────────
  const handleSend = async () => {
    setError('');
    const toList = to.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
    if (toList.length === 0) { setError('Please enter at least one recipient'); return; }

    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    for (const addr of toList) { if (!emailRe.test(addr)) { setError(`Invalid email: ${addr}`); return; } }

    // ◀── Point 1: Domain whitelist validation
    if (restrictDomains) {
      for (const addr of toList) {
        if (!validateEmailDomain(addr)) {
          const domain = addr.split('@')[1];
          setError(`Email domain @${domain} is not allowed. Allowed: ${allowedDomains.join(', ')}`);
          return;
        }
      }
    }

    const ccList = cc.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
    for (const addr of ccList) { if (!emailRe.test(addr)) { setError(`Invalid CC: ${addr}`); return; } }

    // ◀── Point 1: Domain check for CC too
    if (restrictDomains) {
      for (const addr of ccList) {
        if (!validateEmailDomain(addr)) {
          const domain = addr.split('@')[1];
          setError(`CC domain @${domain} is not allowed. Allowed: ${allowedDomains.join(', ')}`);
          return;
        }
      }
    }

    setSending(true);
    try {
      const payload = {
        to: toList,
        cc: ccList.length > 0 ? ccList : undefined,
        subject: subject.trim() || 'VBL Sales AI — Shared Response',
        body: body.trim() || '',
        from_email: fromMode === 'self' && fromEmail ? fromEmail : undefined,
        message_content: {
          question: messageData?.question || '',
          response: messageData?.response || '',
          data: messageData?.data || null,
          columns: messageData?.columns || null,
        },
        conversation_id: messageData?.conversationId || '',
        message_id: messageData?.messageId || '',
      };

      const res = await fetch(`${config.apiBaseUrl}/api/share/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess(true);
        setTimeout(() => onClose(), 1800);
      } else {
        setError(data.error || 'Failed to send email');
      }
    } catch (err) {
      setError('Network error — please try again');
    } finally {
      setSending(false);
    }
  };

  // ── Render ──────────────────────────────────────────────
  if (!isOpen) return null;

  return (
    <div className="ec-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ec-modal">
        {/* Header */}
        <div className="ec-header">
          <h2 className="ec-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" />
            </svg>
            Share via Email
          </h2>
          {composing && <span className="ec-composing-badge">✨ AI composing…</span>}
          <button className="ec-close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Success */}
        {success ? (
          <div className="ec-success">
            <div className="ec-success-icon">✅</div>
            <p>Email sent successfully!</p>
          </div>
        ) : (
          <>
          <div className="ec-body">
            {/* From toggle */}
            <div className="ec-field">
              <label className="ec-label">From</label>
              <div className="ec-from-row">
                <label className="ec-radio-label">
                  <input type="radio" name="fromMode" value="system" checked={fromMode === 'system'}
                    onChange={() => setFromMode('system')} />
                  <span>System (VBL Sales AI Agent)</span>
                </label>
                <label className="ec-radio-label">
                  <input type="radio" name="fromMode" value="self" checked={fromMode === 'self'}
                    onChange={() => { setFromMode('self'); setFromEmail(userEmail || ''); }} />
                  <span>My email {userEmail ? `(${userEmail})` : ''}</span>
                </label>
              </div>
              {fromMode === 'self' && (
                <input type="text" className="ec-input ec-from-input" value={fromEmail}
                  readOnly placeholder="your.name@rjcorp.in" />
              )}
            </div>

            {/* To */}
            <ContactInput label="To" value={to} onChange={setTo} authToken={authToken}
              placeholder="Type name or email — suggestions from Office 365" />
            {/* ◀── Point 1: Domain restriction notice */}
            {restrictDomains && allowedDomains.length > 0 && (
              <div style={{
                padding: '6px 12px', margin: '-8px 0 8px', fontSize: '11px',
                color: '#93370d', background: '#fffaeb', border: '1px solid #fec84b',
                borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <span>🔒</span>
                <span>Only domains from <strong>RJ Corp Group</strong> are allowed.</span>
              </div>
            )}

            {/* CC */}
            <ContactInput label="CC" value={cc} onChange={setCc} authToken={authToken}
              placeholder="Optional" />

            {/* Subject + AI Compose button */}
            <div className="ec-field">
              <label className="ec-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Subject
                {composing && <span className="ec-field-loading">generating…</span>}
                {/* ◀── Point 4: Manual AI compose button */}
                {!composing && (
                  <button
                    type="button"
                    onClick={handleAICompose}
                    style={{
                      marginLeft: 'auto', padding: '3px 10px', fontSize: '11px',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      color: '#fff', border: 'none', borderRadius: '12px',
                      cursor: 'pointer', fontWeight: 600, display: 'flex',
                      alignItems: 'center', gap: '4px',
                    }}
                    title="Generate subject and body using AI"
                  >
                    ✨ Compose with AI
                  </button>
                )}
              </label>
              <input type="text" className="ec-input" value={subject}
                onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" />
            </div>

            {/* Body — message textarea */}
            <div className="ec-field ec-field--grow">
              <label className="ec-label">
                Message
                {composing && <span className="ec-field-loading">generating…</span>}
                <button className={`ec-mic ${isRecording ? 'ec-mic--active' : ''}`}
                  onClick={toggleVoice} title={isRecording ? 'Stop dictation' : 'Dictate'} type="button">
                  {isRecording ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  )}
                  {isRecording && <span className="ec-mic-pulse" />}
                </button>
              </label>
              <textarea className="ec-textarea" value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={composing ? 'AI is composing your message…' : 'Type or dictate your message…'}
                rows={8} />
            </div>
          </div>

          {/* Footer: attachment + error + actions — OUTSIDE ec-body so they never scroll or overlap */}
          <div className="ec-footer">
            {/* Attachment */}
            <div className="ec-attachment">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              <span className="ec-attachment-name">📎 VBL_Response.pdf</span>
              <span className="ec-attachment-note">AI response + data table</span>
            </div>

            {error && <div className="ec-error"><span>⚠️</span> {error}</div>}

            {/* Actions */}
            <div className="ec-actions">
              <button className="ec-cancel" onClick={onClose} disabled={sending}>Cancel</button>
              <button className="ec-send" onClick={handleSend} disabled={sending || composing || !to.trim()}>
                {sending ? (
                  <><span className="ec-spinner" /> Sending…</>
                ) : (
                  <><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg> Send Email</>
                )}
              </button>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailComposer;
